import React from 'react';
import { Box, Grid, Button } from '@mui/material';
import LateTimeLcoationicon from 'assets/shift-monitoring/location-late.svg';
import OnTimeLocationIcon from 'assets/shift-monitoring/location-on-time.svg';

import {
  Container,
  SectionTitle,
  FieldLabel,
  FieldValue,
} from './styles';

interface DetailItemProps {
  label: string;
  value: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <Box>
    <FieldLabel>{label}</FieldLabel>
    <FieldValue>{value}</FieldValue>
  </Box>
);

interface ShiftDetailsProps {
  date: string;
  position: string;
  startTime: string;
  endTime: string;
  numberOfWorkers: string;
  experience: number | string;
  appointedMember: string;
  shift_id?: number | null;
  createdDate?: string;
  facilityName?: string;
  // Completed Shift Props
  clockInTime?: string;
  clockOutTime?: string;
  checkInMethod?: string;
  complianceStatus?: string;
  totalHours?: string;
  mapImageUrl?: string;
  isEarly?: boolean;
  isLate?: boolean;
  assignedDate?: string;
  assignmentStatus?: string;
  isFacilityReject?: boolean;
  onApproveAttendance?: () => void;
  workerRemarks?: string;
  isSleepover?: boolean;
  label?: string;
}

const ShiftDetails: React.FC<ShiftDetailsProps> = ({
  date,
  position,
  startTime,
  endTime,
  numberOfWorkers,
  createdDate,
  assignedDate,
  assignmentStatus,
  clockInTime,
  clockOutTime,
  checkInMethod,
  complianceStatus,
  totalHours,
  mapImageUrl,
  isEarly,
  isLate,
  isFacilityReject,
  onApproveAttendance,
  workerRemarks,
  isSleepover,
  label,
}) => {
  // Logic simplified for view-only component matching new design

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <SectionTitle>Shift Details</SectionTitle>
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailItem label="Shift Created" value={createdDate || '-'} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DetailItem label="Worker Assigned" value={assignedDate || '-'} />
        </Grid>

        {assignmentStatus && (
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailItem label="Assignment Status" value={assignmentStatus} />
          </Grid>
        )}

        {workerRemarks && (
          <Grid size={{ xs: 12, md: 12 }}>
            <DetailItem label="Worker Remarks" value={workerRemarks} />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <DetailItem label="Scheduled Date" value={date} />
        </Grid>

        {clockInTime && clockOutTime ? (
          <Grid size={{ xs: 12, md: 6 }}>
            <FieldLabel>Clock In / Out</FieldLabel>
            <Box display='flex' alignItems='center' gap={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <FieldValue>{clockInTime}</FieldValue>
                <img src={isLate ? LateTimeLcoationicon : OnTimeLocationIcon} alt="location" style={{ width: 16, height: 16 }} />
              </Box>
              <FieldValue>/</FieldValue>
              <Box display="flex" alignItems="center" gap={0.5}>
                <FieldValue>{clockOutTime}</FieldValue>
                <img src={isEarly ? LateTimeLcoationicon : OnTimeLocationIcon} alt="location" style={{ width: 16, height: 16 }} />
              </Box>
            </Box>
          </Grid>
        ) : (
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailItem label="Scheduled Time" value={`${startTime} - ${endTime}`} />
          </Grid>
        )}

        {/* If completed, show Total Hours instead of Role in this slot? Or Layout shifts. 
            User screenshot: Total Hours is below Scheduled Date. 
            Role is NOT in the screenshot for completed. 
            Compliance Status is below Clock In/Out.
        */}

        {complianceStatus ? (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <DetailItem label="Total Hours" value={totalHours || "8 hours"} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DetailItem label="Compliance Status" value={complianceStatus} />
            </Grid>

            <Grid size={12}>
              <DetailItem label="Check-in-Method" value={checkInMethod || "Mobile App (Android)"} />
            </Grid>
          </>
        ) : (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <DetailItem label="Role" value={position} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DetailItem label="No of Workers" value={numberOfWorkers} />
            </Grid>
          </>
        )}

        {label && (
          <Grid size={12}>
            <DetailItem label="Label" value={label} />
          </Grid>
        )}

        {isSleepover && (
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailItem label="Sleepover" value="Yes" />
          </Grid>
        )}
      </Grid>

      {isFacilityReject && onApproveAttendance && (
        <Box mt={3}>
          <Button
            fullWidth
            variant="contained"
            sx={{ backgroundColor: '#479577', '&:hover': { backgroundColor: '#3b7a62' } }}
            onClick={onApproveAttendance}
          >
            Approve Attendance
          </Button>
        </Box>
      )}

      {mapImageUrl && (
        <Box mt={2}>
          <img
            src={mapImageUrl}
            alt="Shift Location Map"
            style={{ width: '100%', height: 'auto', borderRadius: '8px', display: 'block' }}
          />
        </Box>
      )}
    </Container>
  );
};

export default ShiftDetails;
